import { z } from 'zod';

import { isUnparseableDueDate, startOfUtcDay } from '@/utils/date-utils';

/**
 * A BUSINESS DATE — a calendar day, persisted as midnight UTC ms.
 *
 * Accepts any instant inside the intended UTC day and snaps it down, so the
 * server is the single backstop for every writer. Clients have repeatedly got
 * this wrong (local noon, local midnight, a raw `Date.now()`), and the failure
 * is silent: an off-grid value still renders as the right day but falls outside
 * an inclusive `<= periodEnd` bound, so the row vanishes from reports.
 *
 * Use for `postedOn` / `issuedOn` / `dueOn` / `receivedOn` / `paidOn` /
 * `checkDate` / `returnedOn` / `occurredOn` / `openedOn` / `closedOn` /
 * `transferDate` / `startOn` / `endOn`. Do NOT use for real moments-in-time
 * (`createdAt`, `voidedAt`) — those legitimately carry a time of day.
 */
// Deliberately NOT `.int()`: `validateBody` FAILS OPEN — a single rejected field
// makes it return the raw body, skipping this transform AND every sibling
// `.default()`. Flooring a stray fractional ms is strictly safer than rejecting
// it. Bounds are checked AFTER the transform, so `.positive()` can't certify a
// value the transform then snaps to 0.
export const BusinessDateSchema = z.number().finite().transform((n) => startOfUtcDay(Math.floor(n)));
export const OptionalBusinessDateSchema = BusinessDateSchema.optional();
export const NullableBusinessDateSchema = BusinessDateSchema.nullable().optional();
/** Business date that must land on/after the epoch (preserves a former `.nonnegative()`). */
export const NonNegativeBusinessDateSchema = BusinessDateSchema.refine(
	(n) => n >= 0, { message: 'Date must be on or after 1970-01-01' },
);
/** Business date that must be strictly after the epoch (preserves a former `.positive()`). */
export const PositiveBusinessDateSchema = BusinessDateSchema.refine(
	(n) => n > 0, { message: 'Date must be after 1970-01-01' },
);

/**
 * Ids are opaque strings, NOT uuids.
 *
 * These demanded `z.string().uuid()`, which this app's own ids do not satisfy: users carry
 * 16-hex ids like `28c678642009ecc3`, seeded GL codes are `gl_def_<code>`, banks are
 * `bank_<uuid>`, and test fixtures use readable slugs. Only `crypto.randomUUID()` rows
 * match.
 *
 * It never surfaced because every consumer went through `validateBody`, which FAILS OPEN —
 * the uuid check rejected real payloads and the raw body was passed through regardless, so
 * the rule was written but never enforced. Enforcing it (safeParse) 400s association
 * assignment, time-entry creation and more, which is exactly what it did before this was
 * corrected.
 *
 * Non-empty is the real invariant: an id must be present, and the FK decides if it exists.
 */
export const IdSchema = z.string().min(1).nullish();
export const RequiredIdSchema = z.string().min(1);

/**
 * A timestamp arrives as an ISO string OR as epoch milliseconds — both are real.
 *
 * `z.string().datetime()` rejected every numeric one, which includes the work-order
 * writes the app itself sends (`createdAt: <ms>`); see `parseDateToMs`, which exists
 * precisely because this field is mixed on the wire. Fail-open validation hid it: the
 * numeric form failed the check and was passed through anyway.
 *
 * Shape only. Whether a value is a SENSIBLE date, and whether the caller may set it at
 * all, stays with the handler — `created_at` and `order_number` are field-locked there.
 */
export const TimestampSchema = z.union([z.string(), z.number()])
	/**
	 * Type alone is not enough. `completedAt: "not-a-date"` satisfied `z.string()`, and the
	 * handler then bound `new Date(v).getTime()` — NaN — producing a 500 where fail-closed
	 * validation is supposed to return a 400. Attachments silently stored null instead.
	 */
	.refine(
		(v) => (typeof v === 'number' ? Number.isFinite(v) : !Number.isNaN(Date.parse(v))),
		{ message: 'must be an ISO date string or epoch milliseconds' },
	)
	.nullish();
export const RequiredTimestampSchema = z.string().datetime();

/**
 * A `dueDate` on the wire: epoch ms, a numeric or ISO string (older iOS builds), `''` or null to
 * clear. The handler stores its ET business day via `normalizeDueDate`. Anything that names no
 * date is a 400, so a garbage value can never silently clear an existing due date.
 */
export const DueDateSchema = z.union([z.number(), z.string()])
	.refine((v) => !isUnparseableDueDate(v), { message: 'must be a date (epoch ms or ISO string)' })
	.nullish();

/**
 * An email, or EMPTY — the vendor and contact editors submit `''` to clear the field, not
 * null. `.email()` alone rejected that, turning a working clear into a 400 the moment
 * validation stopped failing open. Empty is normalised to undefined so handlers that write
 * `?? null` keep clearing the column.
 */
/**
 * `''` normalizes to NULL, not `undefined` — the difference between CLEARING and OMITTING.
 *
 * Every editor submits the whole form, so a user who empties the email box sends `email: ''`.
 * The update handlers build their SET list with `if (val !== undefined)` (see
 * `api/vendors.ts`), so mapping `''` to `undefined` made the field indistinguishable from one
 * that was never sent: the request 200'd and the old address stayed. Mapping it to `null`
 * writes SQL NULL and actually clears it.
 *
 * Only `EmailSchema` does this, and only because it must ALSO reject malformed addresses —
 * `''` fails `.email()`, so it needs the explicit `z.literal('')` branch to get this far. Every
 * table reached through this schema has a nullable email column; the one NOT NULL column
 * (`users.email`) is declared with {@link RequiredEmailSchema} and never sees this transform.
 */
export const EmailSchema = z.union([z.string().email(), z.literal('')])
	.nullish()
	.transform((v) => (v === '' ? null : v));
export const RequiredEmailSchema = z.string().email();

export const PhoneSchema = z.string().nullish();
export const RequiredPhoneSchema = z.string();

export const AddressSchema = z.string().nullish();
export const RequiredAddressSchema = z.string();

export const CitySchema = z.string().nullish();
export const RequiredCitySchema = z.string();

export const StateSchema = z.string().nullish();
export const RequiredStateSchema = z.string();

export const ZipCodeSchema = z.string().nullish();
export const RequiredZipCodeSchema = z.string();

export const NotesSchema = z.string().nullish();
export const RequiredNotesSchema = z.string();

/**
 * Coordinates are NULLABLE, and null is the common case.
 *
 * `components/associations/form.tsx` sends `lat: null, lng: null` whenever geocoding
 * returns nothing — which is most of the book: 12 of 13 associations in production data
 * have no coordinates. `.optional()` rejected that, so once `parseBody` started enforcing,
 * saving any such association 400'd, the dues write behind it never fired, and the screen
 * showed no error at all — only the unrelated "Could not find coordinates" line.
 *
 * Found by a browser walk, not by the suites: the fixtures all have coordinates.
 */
export const LatitudeSchema = z.number().nullish();
export const LongitudeSchema = z.number().nullish();

/**
 * Zero is a real cost. The work-order editor accepts non-negative values and sends
 * `estimatedCost: 0`, and the handler persists it — `.positive()` turned that into a 400
 * the moment validation stopped failing open.
 */
export const CostSchema = z.number().nonnegative().nullish();
export const RequiredCostSchema = z.number().positive();

export const NumberOfUnitsSchema = z.number().int().positive().nullish();
export const RequiredNumberOfUnitsSchema = z.number().int().positive();

// Common validation utilities
export const createFailSafeSchema = <T extends z.ZodType>(schema: T) => {
	return (schema as any).passthrough() as T;
};

// Helper to create create/update schemas with common patterns
export const createCreateSchema = <T extends z.ZodRawShape>(shape: T) => {
	return createFailSafeSchema(z.object(shape));
};

export const createUpdateSchema = <T extends z.ZodRawShape>(shape: T) => {
	return createFailSafeSchema(z.object(shape).partial());
};

/**
 * Optional string — and `null` is a legitimate value, meaning CLEAR THIS FIELD.
 *
 * `z.string().optional()` accepts `undefined` but REJECTS `null`, while the clients send
 * `null` precisely to blank a note, an email or an address. Same story as the id schemas:
 * fail-open validation meant the rejection was never felt until the parse was enforced,
 * at which point clearing any optional field became a 400.
 */
export const OptionalStringSchema = z.string().nullish();
export const OptionalNonEmptyStringSchema = z.string().min(1).nullish();

// Helper for required string fields
export const RequiredStringSchema = z.string().min(1);
export const RequiredNonEmptyStringSchema = z.string().min(1);

// Helper for optional number fields
export const OptionalNumberSchema = z.number().nullish();
export const OptionalPositiveNumberSchema = z.number().positive().nullish();

// Helper for required number fields
export const RequiredNumberSchema = z.number();
export const RequiredPositiveNumberSchema = z.number().positive();

// Helper for boolean fields
export const OptionalBooleanSchema = z.boolean().nullish();
export const RequiredBooleanSchema = z.boolean();

// Helper for date fields
export const OptionalDateSchema = z.string().datetime().optional();
export const RequiredDateSchema = z.string().datetime();

// Helper for file fields
export const FileSchema = z.instanceof(File).optional();
export const RequiredFileSchema = z.instanceof(File);

// Helper for FormData fields
export const FormDataSchema = z.instanceof(FormData);
export const RequiredFormDataSchema = z.instanceof(FormData);
